import React from 'react';
import { useTranslation } from 'react-i18next';

import { PERMISSIONS } from 'api/auth/constants';
import Link from 'components/Link';
import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';
import { PERMISSION_TYPE, useRBAC } from 'hooks/useRBAC';
import { copyToClipBoard } from 'utils/common';

import { CopyCellPopoverContainer, HelperPopover } from '../../styled';

import { Container, CopyButton, TextWrapper } from './style';

type ICopyCell<T> = {
  conditionType?: PERMISSION_TYPE;
  enableFullText?: boolean;
  href?: string;
  item: T;
  permissions?: PERMISSIONS[];
  target?: '_blank';
  text: string;
  to?: string;
};

const TEXT_LENGTH = 10;

const CopyCell = <T,>({
  conditionType,
  enableFullText,
  href,
  permissions,
  target,
  text,
  to,
}: ICopyCell<T>) => {
  const { t } = useTranslation();

  const { checkPermission } = useRBAC();

  const isCanRedirect = checkPermission({
    conditionType,
    list: permissions,
  });

  const isFullText = text.length <= TEXT_LENGTH || enableFullText;

  const normalizeText = React.useMemo(() => {
    if (isFullText) {
      return text;
    }
    return `${text.slice(0, 5)}...${text.slice(-5)}`;
  }, [isFullText, text]);

  const onCopy = React.useCallback(() => copyToClipBoard(text), [text]);

  if (!text) {
    return <>-</>;
  }

  return (
    <Container>
      <CopyButton
        closeTooltipIn={0}
        iconSize={14}
        onClick={onCopy}
        showTooltipOnClick={false}
        startIcon={<ICONS_MAP.Copy />}
        testIds={{ container: 'copyButton' }}
        tooltipText={t('common.copied')}
        variant='icon'
      />
      {(to || href) && (!permissions || (permissions && isCanRedirect)) ? (
        <HelperPopover
          asContainer={CopyCellPopoverContainer}
          component={text}
          disabled={isFullText}
          immediateAnimation
          openDelay={300}
          positionGap={14}
          showOnClick={false}
        >
          <div>
            <Link href={href} target={target} to={to}>
              {normalizeText}
            </Link>
          </div>
        </HelperPopover>
      ) : (
        <HelperPopover
          asContainer={CopyCellPopoverContainer}
          component={text}
          disabled={isFullText}
          positionGap={14}
        >
          <TextWrapper $isPointer={!isFullText}>{normalizeText}</TextWrapper>
        </HelperPopover>
      )}
    </Container>
  );
};

export default appReactMemo(CopyCell);
